import React, { useEffect, useState } from 'react';

import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import FormPasswordInput from 'components/FormPasswordInput';
import FormTextInput from 'components/FormTextInput';
import SimpleFormLayout from 'components/SimpleFormLayout';
import authEndpoints from 'config/api/auth';
import emailValidator from 'config/validators/emailValidator';
import useApiCall from 'hooks/useApiCall';
import auth_messages from 'messages/auth_messages';
import otp_messages from 'messages/otp_messages';
import validation_messages from 'messages/validation_messages';
import { useAuthContext } from 'reactContext/AuthContext';

const ACCOUNT_LOCKED_STATUS = 'account locked up';
const LEGACY_LOCK_STORAGE_KEY = 'locked_to';
const BACKEND_DATETIME_PATTERN = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/;

export const parseAccountOpenTime = value => {
  if (typeof value !== 'string') return null;

  const match = value.match(BACKEND_DATETIME_PATTERN);
  if (!match) return null;

  const [, date, time, fraction = '', timezone] = match;
  const milliseconds = fraction ? `.${fraction.slice(0, 3).padEnd(3, '0')}` : '';
  const timestamp = Date.parse(`${date}T${time}${milliseconds}${timezone}`);
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const getLockoutOpenTime = (status, data, currentTime = Date.now()) => {
  if (status !== 403 || data?.status !== ACCOUNT_LOCKED_STATUS) return null;

  const openTime = parseAccountOpenTime(data.account_open_time);
  return openTime !== null && openTime > currentTime ? openTime : null;
};

export const getRetryAfterOpenTime = (status, headers, currentTime = Date.now()) => {
  if (status !== 429) return null;

  const retryAfter = typeof headers?.get === 'function' ? headers.get('retry-after') : headers?.['retry-after'] ?? headers?.['Retry-After'];
  if (typeof retryAfter === 'number' || (typeof retryAfter === 'string' && /^\d+(?:\.\d+)?$/.test(retryAfter.trim()))) {
    const retryAfterSeconds = Number(retryAfter);
    return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? currentTime + Math.ceil(retryAfterSeconds * 1000) : null;
  }

  if (typeof retryAfter !== 'string') return null;
  const retryAfterTime = Date.parse(retryAfter);
  return Number.isFinite(retryAfterTime) && retryAfterTime > currentTime ? retryAfterTime : null;
};

export const getRemainingSeconds = (openTime, currentTime = Date.now()) => Math.max(0, Math.ceil((openTime - currentTime) / 1000));

const LoginForm = () => {
  const { t } = useTranslation();
  const [generalError, setGeneralError] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [blockedUntil, setBlockedUntil] = useState(null);

  const { apiCall, sending } = useApiCall();
  const { onLogin } = useAuthContext();

  useEffect(() => {
    if (blockedUntil === null) return undefined;

    let intervalId = null;
    const stopInterval = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const updateRemainingTime = () => {
      const nextRemainingSeconds = getRemainingSeconds(blockedUntil);
      if (nextRemainingSeconds === 0) {
        stopInterval();
        setRemainingSeconds(null);
        setBlockedUntil(null);
        setGeneralError(null);
        return;
      }
      setRemainingSeconds(nextRemainingSeconds);
    };

    updateRemainingTime();
    if (getRemainingSeconds(blockedUntil) > 0) {
      intervalId = setInterval(updateRemainingTime, 1000);
    }

    return stopInterval;
  }, [blockedUntil]);

  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_LOCK_STORAGE_KEY);
    } catch {
      // Browser storage can be unavailable; lockout enforcement remains server-side.
    }
  }, []);

  const onSubmit = async values => {
    const data = values;
    data.email = data.email.toLowerCase();
    const { data: responseData, headers, status } = await apiCall(authEndpoints.login(), { data }, { showError: false });
    if (status === 401) {
      setGeneralError(t(...validation_messages.errors.credentials));
      return;
    }
    if (status === 403) {
      const openTime = getLockoutOpenTime(status, responseData);
      if (openTime !== null) {
        setBlockedUntil(openTime);
        setGeneralError(t(...validation_messages.errors.too_many_attempts));
      } else {
        setGeneralError(t(...validation_messages.errors.credentials));
      }
      return;
    }
    if (status === 429) {
      const openTime = getRetryAfterOpenTime(status, headers);
      if (openTime !== null) setBlockedUntil(openTime);
      setGeneralError(t(...validation_messages.errors.rate_limited));
      return;
    }
    if (status >= 400) {
      setGeneralError(responseData.detail);
      return;
    }

    const result = await onLogin(responseData);

    if (result !== 'success') {
      setGeneralError(t(...otp_messages.otp_auth_failed));
    }
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit,
    validationSchema: yup.object({
      email: emailValidator({
        required: t(...validation_messages.email_required),
        valid: t(...validation_messages.email_valid),
      }),
      password: yup.string(t(...validation_messages.password_required)).required(t(...validation_messages.password_required)),
    }),
  });

  const onFormChange = () => {
    if (blockedUntil === null) setGeneralError(null);
  };

  useEffect(() => {
    // validate on autofill
    formik.validateForm();
  }, []);

  return (
    <SimpleFormLayout
      disabled={!formik.isValid}
      generalError={generalError}
      onChange={onFormChange}
      onSubmit={formik.handleSubmit}
      remainingSeconds={remainingSeconds}
      sending={sending}
      submitLabel={t(...auth_messages.navigation.login)}
      title={t(...auth_messages.navigation.sign_in)}
    >
      <FormTextInput formik={formik} id='email' label={t(...auth_messages.email)} required type='email' />
      <FormPasswordInput formik={formik} id='password' label={t(...auth_messages.password)} required />
    </SimpleFormLayout>
  );
};

export default LoginForm;
